import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DeviceDetectorService } from 'ngx-device-detector';
import { filter, take } from 'rxjs';
import { VERSION } from '../../../../version';
import { MenuItemComponent } from '../../../basic/menu/menu-item/menu-item.component';
import { ContentModalComponent } from '../../../basic/modal/content-modal/content-modal.component';
import { BoardHolderService } from '../../../core/board/board.holder.service';
import { SentryService } from '../../../core/sentry/sentry.service';
import { StripeProductStatus, StripeProductType } from '../../../core/stripe/product.stripe';
import { TextIconComponent } from '../../../core/text-icon/text-icon.component';
import { UserClerkService } from '../../../core/user/user-clerk.service';
import { WorkspaceHolderService } from '../../../core/workspace/workspace.holder.service';
import { StringUtils } from '../../../shared/utils/string-utils';

@Component({
  selector: 'app-main-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzLayoutModule,
    NzMenuModule,
    NzButtonModule,
    NzIconModule,
    RouterLink,
    NzAvatarModule,
    NzDropDownModule,
    NzModalModule,
    NzInputModule,
    NzToolTipModule,

    MatTooltipModule,

    TextIconComponent,
    ContentModalComponent,
    MenuItemComponent,
  ],
  providers: [NzMenuModule],
  templateUrl: './main-sidebar.component.html',
  styleUrl: './main-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainSidebarComponent {
  isSendFeedbackVisible = false;

  StripeProductStatus = StripeProductStatus;
  StripeProductType = StripeProductType;

  isSmall = signal<boolean>(false);

  isCollapsed = false;

  constructor(
    private router: Router,
    private userClerkService: UserClerkService,
    private sentryService: SentryService,
    private activatedRoute: ActivatedRoute,
    private workspaceHolderService: WorkspaceHolderService,
    private boardHolderService: BoardHolderService,
    private breakpointObserver: BreakpointObserver,
    private deviceService: DeviceDetectorService
  ) {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      if (this.router.url.startsWith('/simulated-meetings')) {
        this.isCallLibraryOpen.set(true);
      } else if (this.router.url.startsWith('/customers')) {
        this.isMyMaterialsOpen.set(true);
      }
    });
  }

  isCallLibraryOpen = signal<boolean>(true);
  isMyMaterialsOpen = signal<boolean>(true);
  isAnalyticsOpen = signal<boolean>(true);

  ngOnInit() {
    this.breakpointObserver
      .observe([Breakpoints.Handset, Breakpoints.Small, Breakpoints.XSmall])
      .subscribe((result) => {
        this.isSmall.set(result.matches);
        this.isCollapsed = result.matches;
      });
  }

  ngAfterViewInit() {}

  workspaceName = computed(() => {
    return StringUtils.getWithoutFirstEmoji(this.currentWorkspace().title);
  });

  currentWorkspace = computed(() => {
    return this.workspaceHolderService.currentWorkspace();
  });

  paidStatus = computed(() => {
    return this.workspaceHolderService.paidStatus();
  });

  portraitUrl = computed(() => {
    return this.userClerkService.portraitUrl();
  });

  firstName = computed(() => {
    return this.userClerkService.getUser()?.firstName;
  });

  credits = computed(() => {
    return this.userClerkService.credits();
  });

  onMembersClick() {
    this.router.navigate(['/members']);
  }

  onProjectSettingsClick() {
    this.router.navigate(['/workspace-settings']);
  }

  onUsageBillingClick() {
    this.router.navigate(['/usage-billing']);
  }

  onLogoutClick() {
    this.userClerkService.signOut$().pipe(take(1)).subscribe();
  }

  onContactSupportClick() {
    this.email = this.userClerkService.getEmail();
    this.name = this.boardHolderService.myPersonalBoard()?.userBoardConfig?.preferredName;

    this.isSendFeedbackVisible = true;
  }

  onGetMorePointsClick() {
    this.router.navigate(['/pricing']);
  }

  onPointsClick() {
    this.router.navigate(['/history']);
  }

  onWalletClick() {
    this.router.navigate(['/history']);
  }

  onMyAccountClick() {
    this.router.navigate(['/my-account']);
  }

  onSwitchWorkspacesClick() {
    this.router.navigate(['/switch-workspaces']);
  }

  feedback?: string;
  name?: string;
  email?: string;

  handleSendFeedbackCancel() {
    this.isSendFeedbackVisible = false;
    this.feedback = undefined;
  }

  handleSendFeedbackOk() {
    this.isSendFeedbackVisible = false;
    if (this.feedback) {
      this.sentryService.sendFeedback(this.name ?? '', this.email ?? '', this.feedback);
    }

    this.feedback = undefined;
  }

  version() {
    return VERSION;
  }
}
